/**
 * @file users.api.js
 * @module UsersAPI
 * @description HTTP API wrapper for user management endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios";

export const UsersAPI = {
    /**
     * Retrieve users with pagination and optional query.
     * @param {number} limit - Number of items per page.
     * @param {number} page - Page number.
     * @param {string} [query=""] - Optional query string for filtering.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to users list.
     */
    getUsers: (limit, page, query = "", token = "") => {
        return axiosInstance(token).get(`/users?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Create a new user.
     * @param {Object} body - User payload.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to created user.
     */
    createUsers: (body, token = "") => {
        return axiosInstance(token).post(`/users?origin=europeshuttle`, body);
    },

    /**
     * Get a single user by id.
     * @param {string} _id - User identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to user data.
     */
    getOneUser: (_id, token = "") => {
        return axiosInstance(token).get(`/users/${_id}`);
    },

    /**
     * Delete a user by id.
     * @param {string} _id - User identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating deletion result.
     */
    deleteUser: (_id, token = "") => {
        return axiosInstance(token).delete(`/users/${_id}`);
    },

    /**
     * Update a user by id.
     * @param {string} _id - User identifier.
     * @param {Object} body - Updated user payload.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated user.
     */
    updateUser: (_id, body, token = "") => {
        return axiosInstance(token).put(`/users/${_id}?origin=europeshuttle`, body);
    },

    /**
     * Patch attributes of a user by id.
     * @param {string} _id - User identifier.
     * @param {Object} body - Partial payload with attributes to update.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated user.
     */
    updateSameAttribute: (_id, body, token = "") => {
        return axiosInstance(token).patch(`/users/${_id}`, body);
    },

    /**
     * Search users with optional pagination object.
     * @param {string} query - Search query string.
     * @param {Object} pagination - Pagination object or empty object.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to search results.
     */
    searchUsers: (query, pagination, token = "") => {
        if (Object.entries(pagination).length === 0) {
            return axiosInstance(token).get(`/search/users?search=${query}`);
        } else {
            return (pagination.type === "change") ? axiosInstance(token).get(`/search/users?search=${query}&limit=${limit}&page=${pagination.page}`) : axiosInstance(token).get(`${pagination.url}`);
        }
    },

    /**
     * Delete multiple users.
     * @param {Object} body - Payload describing users to delete.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating deletion result.
     */
    deleteUsersMany: (body, token = "") => {
        return axiosInstance(token).put(`/users/delete/many`, body)
    },

    /**
     * Upload user picture (multipart/form-data).
     * @param {FormData|Object} formData - FormData containing image file.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to upload result.
     */
    uploadUserPicture: (formData, token = "") => {
        return axiosInstance(token).post(`/picture/upload/users`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },

    /**
     * Verify whether a user exists by email.
     * @param {string} email - Email to verify.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to verification result.
     */
    verifyUserExist: (email, token = "") => {
        return axiosInstance(token).get(`/users/${email}/verifyUserExist`);
    },

    /**
     * Resend welcome email to a user.
     * @param {string} userId - User identifier.
     * @param {boolean} [status=true] - Status flag for welcome email.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to send result.
     */
    resendWelcomeEmail: (userId, status = true, token = "") => {
        return axiosInstance(token).post(`/users/${userId}/resendWelcomeEmail`, { status: status });
    },

    /**
     * Send email signature action by user id.
     * @param {string} userId - User identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to send result.
     */
    sendEmailSignatureById: (userId, token = "") => {
        return axiosInstance(token).post(`/users/${userId}/send-email-signature`);
    },
}
